// Drives the plain-text <-> minimal-rich-text toggle in the opportunity
// "Log activity" body field. Only the active mode's inputs are enabled so
// exactly one `activity[body]` value is submitted. On toggle, text carries over
// one-way in each direction, mentions included: plain-text tokens
// ("@[Ada](user:…)") become Tiptap mention nodes and back.
//
// The plain side is a shared/_mention_textarea (Alpine `mentionTextarea`): its
// visible textarea shows "@Ada" and a hidden store input carries the tokens.
//
// Each side also owns an "Ask @X to follow up" control (mention_follow_up.js);
// since only the active side's inputs are posted, the tick, the colleague and
// the due date are handed across with the text.
import { docToStored, storedToHtml } from "./mention_tokens.js";

export default function registerLogActivityBody(Alpine) {
  Alpine.data("logActivityBody", ({ initialMode, editorId } = {}) => ({
    editorMode: initialMode === "rich" ? "rich" : "plain",
    editorId,

    setMode(mode) {
      if (mode !== "plain" && mode !== "rich") return;
      if (mode === this.editorMode) return;

      // Flip the mode first so x-show reveals the target input, then carry the
      // text over on the next tick. The plain textarea must be visible before
      // _copyEditorToPlain dispatches "input": x-autosize measures scrollHeight,
      // which reads 0 on a display:none element and would clamp the textarea
      // shut, clipping the text.
      const followUp = this._followUpHost(this.editorMode)?.followUpSnapshot?.();
      this.editorMode = mode;
      this.$nextTick(() => {
        if (mode === "rich") this._seedEditorFromPlain();
        if (mode === "plain") this._copyEditorToPlain();
        this._followUpHost(mode)?.adoptFollowUp?.(followUp);
      });
      this._persist(mode);
    },

    _tiptapRoot() {
      return this.editorId ? document.getElementById(this.editorId) : null;
    },

    // The live Tiptap editor behind the rich side, if it has booted.
    _editor() {
      const root = this._tiptapRoot();
      return root ? Alpine.$data(root)?.editor || null : null;
    },

    // The mentionTextarea component behind the plain textarea.
    _plain() {
      const host = this.$root.querySelector("[data-plain-body] [data-mention-textarea]");
      return host ? Alpine.$data(host) : null;
    },

    // The component whose follow-up control belongs to `mode`'s editor.
    _followUpHost(mode) {
      if (mode === "plain") return this._plain();
      const root = this._tiptapRoot();
      return root ? Alpine.$data(root) : null;
    },

    // Read from the editor's structured document rather than its HTML, so no
    // markup is ever parsed or reinterpreted.
    _editorStored() {
      const editor = this._editor();
      return editor ? docToStored(editor.getJSON()) : "";
    },

    _seedEditorFromPlain() {
      const stored = (this._plain()?.serialized() || "").trim();
      if (!stored) return;
      if (this._editorStored()) return; // don't clobber content already in the editor
      const root = this._tiptapRoot();
      if (root) {
        root.dispatchEvent(new CustomEvent("tiptap:set-content", { detail: { content: storedToHtml(stored) } }));
      }
    },

    _copyEditorToPlain() {
      const plain = this._plain();
      if (!plain || !this._editor()) return;
      plain.setValue(this._editorStored()); // fires "input" → x-autosize
    },

    _persist(mode) {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      fetch("/activities/set_editor_mode", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "" },
        body: JSON.stringify({ mode })
      }).catch(() => {});
    }
  }));
}
